/**
 * Payment Details Step
 *
 * Second step in the booking flow - payment information collection.
 *
 * Flow:
 * 1. Email Entry → User enters email, clicks "Add Payment Method"
 * 2. Check User → Call /users/guest to check if user exists
 * 3a. Existing User → Show sign-in modal
 * 3b. New User → Show payment form
 * 4. Complete Account → New users complete their account details
 *
 * @module pages/booking/steps
 */

import { needsAccountSetup } from '@butter/library/shared';
import { useMemo, useState, useEffect, type MouseEvent } from 'react';
import { useBooking, useBookingStep, useVisualStep, VISUAL_STEPS } from '~/services/internalApi/booking/context';
import { useBookingPricing, useCreateBooking } from '~/services/internalApi/booking';
import { getCreditBalanceChange, getPaymentTimingChange, getPromotionRejection } from '~/lib/apiClient';
import { usePayment } from '~/features/payment';
import { usePaymentMethodsQuery, reportPaymentClientError, useCardSave, useAutoAdvanceAfterSave, CardEntryForm } from '~/features/payment';
import { useActiveSubscription } from '~/services/internalApi/subscription';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { useHomeClub } from '~/services/internalApi/club';
import { useAuth } from '~/services/auth';
import { useTenantHandle } from '~/services/appRuntime';
import { createClubDateTimeFromSlot, formatTimeRange, timeStringToMinutes } from '@butter/library/datetime';
import { format, parseISO } from 'date-fns';
import { calculateGuestFee, resolveGuestFeeParams, formatGuestFeeLabel, resolvePaymentRequirement } from '@butter/library/pricing';
import { deriveBookingQuote } from './bookingQuote';
import { planPaysHere } from './planCoverage';
import { payerFromQuote } from '@butter/library/subscription';
import { resolveCheckoutCta } from './checkoutCta';
import type { BookingLineItem, BookingPriceSummary } from '@butter/library/pricing';
import { detectPaymentProvider, getPaymentSettings } from '~/lib/utils/paymentProvider';
import { getAppliedTaxRate } from '~/lib/utils/taxCalculation';
import { toNumericUserId } from '~/lib/utils/userIdUtils';
import { toQuotedFees } from '@lgc/database/types';
import type { QuotedFee } from '@lgc/database/types';
import { useTenantNavigate } from '~/lib/navigation';
import {
  addBreadcrumb,
  captureException,
  setUser,
} from '@butter/monitoring';
import { ArrowIcon, CheckmarkIcon } from '@butter/ui/icons';
import { BookingDetailsCard } from "@butter/ui/domains/booking";
import { PriceSummaryCard, PaymentMethodSelector, UNSELECTABLE_METHOD_TYPES } from "@butter/ui/domains/billing";
import { PromoCodeDisclosure } from "@butter/ui/domains/subscriptions";
import { SubscriptionDetailsCard, OverageDetailsCard, describeOverage } from "@butter/ui/domains/subscriptions";
import { CheckoutSection } from "../components/CheckoutSection";
import { EmailPaymentCard } from "../components/EmailPaymentCard";
import { WaiverSection } from "../components/WaiverSection";
import { useWaiverSubmission } from "../components/useWaiverSubmission";
import { waiverRefusalMessage } from "../components/waiverCompletion";
import { EarlyBookingConfirmDialog } from "../components";
import { useEarlyBookingConfirm } from "../shared/useEarlyBookingConfirm";
import { RetryBanner } from "@butter/ui/components/feedback";
import { SignInModal } from "~/ui/layout/SignInModal";
import { userService } from "~/services/user";
import { butterToast } from "@butter/ui/patterns";

/**
 * Payment flow steps
 */
type PaymentStep =
  | "email" // Email entry + "Add Payment Method" button
  | "sign-in" // Existing user authentication
  | "payment"; // Payment method capture

// `id` the card form's `<form>` renders, so the CTA below can submit it
// (Stripe/Square own their form, hidden submit button; see CardEntryForm).
const CARD_FORM_ID = 'checkout-card-form';

// Classes for the single checkout button (see checkoutCta.ts).
const CTA_CLASSES =
  'w-full rounded-full px-6 py-4 text-lg font-semibold bg-theme-600 text-white text-interactive:bg-theme-700 disabled:bg-butter-gray-100 disabled:text-fg-muted disabled:cursor-not-allowed transition-colors';

/**
 * Confirmation Step Component
 *
 * Manages the payment flow with multiple sub-steps:
 * - Email entry
 * - User authentication (if existing)
 * - Payment method capture
 */
export function PaymentDetailsStep() {
  const {
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedBayId,
    guestUser,
    setGuestUser,
    setPaymentMethodToken,
    paymentMethodToken,
    payAtClub,
    setPayAtClub,
    clearBooking,
    guestCount,
  } = useBooking();

  const { goToPreviousStep, goToNextStep, promoCode, setPromoCode } = useBookingStep();
  // The server's copy when a code was auto-dropped at create — exhausted, ended
  // or already spent between the quote and this click. Null = no drop. The
  // banner below the disclosure renders it; clearing `promoCode` requotes at
  // list, so the price the member sees next is the one they will be charged.
  const [promoDropped, setPromoDropped] = useState<string | null>(null);
  const { setVisualStep, setPaymentNeedsNoCard } = useVisualStep();
  const { data: club } = useClubQuery();
  // Payment methods are always created/charged at the user's home club. For
  // authenticated users the payment form must use the home club's credentials,
  // even when visiting another club's portal.
  const { homeClub } = useHomeClub();
  const { state, setEmail } = usePayment();
  const { isAuthenticated, user: authUser } = useAuth();
  const [tenantHandle] = useTenantHandle();
  // Gates the authenticated path. The guest path is gated separately in
  // FinishAccountStep, which is where guests actually create the booking.
  const waiverGate = useWaiverSubmission(club?.id);

  // Early-morning (1-5 AM) times are easily misread as PM — ask once, right
  // before the CTA's own submit (see handleCtaClick).
  const earlyConfirm = useEarlyBookingConfirm(selectedStartTime);

  // Set visual step to CONFIRMATION when component mounts
  useEffect(() => {
    setVisualStep(VISUAL_STEPS.CONFIRMATION);
  }, [setVisualStep]);

  // Fetch active subscription for authenticated users.
  // `isSuccess` matters as much as the value: `hasActiveSubscription` is false while
  // the query is still loading, so without it a member is momentarily treated — and
  // priced — as a walk-in.
  const { activeSubscription, hasActiveSubscription, subscriptionName, holdings, isSuccess: subscriptionSettled } =
    useActiveSubscription({
      userId: authUser?.id
        ? typeof authUser.id === "number"
          ? authUser.id
          : parseInt(authUser.id)
        : undefined,
      enabled: isAuthenticated,
    });
  const isTeamBooking = activeSubscription?.type === 'membership' && activeSubscription.teamId != null;

  // Fetch saved payment methods for authenticated users
  const { data: savedPaymentMethods = [], isLoading: isLoadingPaymentMethods } = usePaymentMethodsQuery({
    enabled: isAuthenticated,
  });

  // Local state for payment flow UI
  const [paymentStep, setPaymentStep] = useState<PaymentStep>("email");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSaved, setPaymentSaved] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);
  const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
  // Written only from CardEntryForm's `onReadyChange` event prop — not a mirror
  // of server state, no effect.
  const [cardReady, setCardReady] = useState(false);
  // Written only from CardEntryForm's `onSubmittingChange` — true across the
  // provider confirm + awaited persist, for both Stripe and Square. The CTA reads
  // this, not PaymentContext's `isProcessing`, which the Stripe form never sets.
  const [cardSubmitting, setCardSubmitting] = useState(false);
  // Written only from CardEntryForm's `onMethodChange` — the Payment Element row
  // the member is on. A bank account can't be charged yet, so a new card saved
  // from the bank row must not be booked with (see handlePaymentSuccess).
  const [newMethodKind, setNewMethodKind] = useState<'card' | 'bank'>('card');

  // Auto-advance a new guest to the next step once their card is saved.
  // Members never set `paymentSaved`: their save chains straight into booking
  // creation, and this hook's member branch would clear `isProcessing` mid-flight.
  useAutoAdvanceAfterSave({
    paymentSaved,
    setPaymentSaved,
    setIsProcessing,
    guestUser,
    isAuthenticated,
    // A guest at a club that collects at the counter continues with no card at all.
    hasSavedPaymentMethod: !!paymentMethodToken || payAtClub,
    goToNextStep,
    surface: "web_booking",
  });

  // Auto-advance authenticated users directly to payment step
  useEffect(() => {
    if (isAuthenticated && paymentStep === "email") {
      console.log(
        "[PaymentDetailsStep] Authenticated user detected, skipping to payment step"
      );
      setPaymentStep("payment");

      // Set monitoring user context for authenticated users
      if (authUser) {
        setUser({
          id:
            typeof authUser.id === "number"
              ? authUser.id
              : parseInt(authUser.id?.toString() || "0"),
          email: authUser.email,
          username: `${authUser.firstName} ${authUser.lastName}`.trim(),
        });

        addBreadcrumb(
          "auth",
          "Authenticated user accessed payment step",
          {
            userId: authUser.id,
            hasActiveSubscription,
            subscriptionType: activeSubscription?.type,
          },
          "info"
        );
      }
    }
  }, [
    isAuthenticated,
    paymentStep,
    authUser,
    hasActiveSubscription,
    activeSubscription,
  ]);

  const navigate = useTenantNavigate();

  // React Query mutation for creating booking
  const createBooking = useCreateBooking({
    onSuccess: (result) => {
      const { booking, accessControl, invoiceId, access } = result;
      console.log(
        "[PaymentDetailsStep] Booking created successfully:",
        result
      );

      // Track successful booking creation with monitoring
      addBreadcrumb(
        "booking",
        "Booking created successfully",
        {
          bookingId: booking.id,
          bayId: booking.bayId,
          userId: booking.userId,
          paymentAmount: booking.paymentAmount,
        },
        "info"
      );

      // Clear booking context to prevent stale data on next booking
      clearBooking();
      console.log("[PaymentDetailsStep] Booking context cleared");

      // Navigate with booking + access-control data in state (the confirmation page reads
      // the door PIN from accessControl.accessCode).
      // `pricing` mirrors the subscription flow's shape (SubscriptionReviewStep) and is
      // what makes the confirmation page's Guest Fee row render — without it that row
      // is unreachable, so a member never saw the fee on their receipt.
      // useTenantNavigate carries only the preserved allow-list (tenant + club)
      // — never a wholesale copy, which would carry a promo link's code onward.
      navigate(`/booking/confirmed/${booking.id}`, {
        state: {
          booking,
          accessControl,
          access,
          invoiceId,
          paymentMethodId: paymentMethodToken,
          pricing: {
            subtotal: planPays
              ? subscriptionPricing.overageCharge + quotedGuestFee
              : subtotal,
            tax: planPays ? subscriptionPricing.overageTax : tax,
            total: planPays ? subscriptionPricing.finalTotal : walkInTotal,
            taxRate,
            guestFee: quotedGuestFee,
          },
        },
      });
    },
    // The API answered `success: false`. A promo refused at reserve lands here
    // and is NOT an error: the booking was not made, the code is dropped, and
    // the member re-confirms at list. The alternative — keeping the booking and
    // charging list — changes the price after they clicked pay.
    onFailure: (result) => {
      const rejection = getPromotionRejection(result);
      if (rejection) {
        setPromoCode(null);
        setPromoDropped(rejection.message);
        setIsProcessing(false);
        return;
      }
      // The club changed how it takes payment since the quote. Nothing was booked or
      // charged: re-quote so the step redraws — a card form where it said "pay at the
      // club", or the Due at Club card where it asked for a card.
      const timingChange = getPaymentTimingChange(result);
      if (timingChange) {
        void refetchPricing();
        butterToast.error(timingChange);
        setIsProcessing(false);
        return;
      }
      // The member's credit no longer covers the booking (spent since the quote). Nothing
      // was booked or charged: re-quote so the card step comes back (credits D30).
      const creditChange = getCreditBalanceChange(result);
      if (creditChange) {
        void refetchPricing();
        butterToast.error(creditChange);
        setIsProcessing(false);
        return;
      }
      console.error("[PaymentDetailsStep] Booking refused:", result.error?.code, result.message);
      if ((result.httpStatus ?? 0) >= 500) {
        captureException(new Error(result.message), {
          level: "error",
          tags: { operation: "create_booking", user_type: isAuthenticated ? "authenticated" : "guest" },
          extra: { code: result.error?.code, httpStatus: result.httpStatus },
        });
      }
      butterToast.error(result.message || "Failed to create booking. Please try again.");
      setIsProcessing(false);
    },

    onError: (error) => {
      console.error("[PaymentDetailsStep] Failed to create booking:", error);

      // Capture booking creation error with full context
      captureException(error, {
        level: "error",
        tags: {
          operation: "create_booking",
          user_type: isAuthenticated ? "authenticated" : "guest",
          has_overage: !!subscriptionPricing?.hasOverage,
          has_active_subscription: hasActiveSubscription,
        },
        extra: {
          bookingContext: {
            selectedBayId,
            selectedDate: selectedDate,
            selectedStartTime,
            selectedEndTime,
            durationMinutes,
            subtotal,
            total,
            hasActiveSubscription,
            subscriptionType: activeSubscription?.type,
          },
        },
      });

      butterToast.error(
        error.message || "Failed to create booking. Please try again."
      );
      setIsProcessing(false);
    },
  });

  // One lock for the checkout button and every control that could leave or
  // re-render the card form: the card save (confirm + persist), the page's own
  // waiver/booking work, and the booking mutation itself. Unmounting the form
  // mid-save would spend the single-use SetupIntent without persisting it.
  const busy = cardSubmitting || isProcessing || createBooking.isPending;

  // Resolve the payment settings the form should be initialized with.
  // Authenticated users → home club (tokens must target the home club's provider).
  // Guests have no home club yet — they're enrolling at the current club — so fall
  // back to the visited club's settings.
  const effectivePaymentSettings = useMemo(
    () => getPaymentSettings(isAuthenticated ? homeClub : club),
    [isAuthenticated, homeClub, club]
  );

  // Detect payment provider from the effective (home or current) club settings
  const paymentProvider = useMemo(
    () => detectPaymentProvider(effectivePaymentSettings),
    [effectivePaymentSettings]
  );

  // The user (or guest) the card is saved for — needed by the Stripe SetupIntent.
  const cardUserIdRaw = authUser?.id ?? guestUser?.id;
  const cardUserId = useMemo(
    () => (cardUserIdRaw != null ? toNumericUserId(cardUserIdRaw) : undefined),
    [cardUserIdRaw]
  );

  // The new-card form is shown whenever we're NOT showing the saved-methods list.
  // A selection counts as "has saved methods" too: when a member's first card is
  // saved and selected, the spent form must unmount now, not when the refetch lands.
  const showingNewPaymentForm = !(
    isAuthenticated &&
    !showNewPaymentForm &&
    (savedPaymentMethods.length > 0 || selectedPaymentMethodId != null)
  );

  // Stripe SetupIntent wiring + the shared confirm/save primitive. The SetupIntent
  // is fetched only while the Stripe card form is actually visible.
  const {
    stripeSetupIntent,
    stripeSetupIntentLoading,
    stripeSetupIntentError,
    retryStripeSetupIntent,
    saveCard,
  } = useCardSave({
    userId: cardUserId,
    clubId: club?.id,
    tenantHandle: tenantHandle || undefined,
    paymentProvider,
    stripeSetupEnabled:
      paymentStep === "payment" &&
      paymentProvider === "stripe" &&
      showingNewPaymentForm,
  });

  // Auto-select default payment method or first one for authenticated users
  useEffect(() => {
    if (
      isAuthenticated &&
      savedPaymentMethods.length > 0 &&
      !selectedPaymentMethodId &&
      !showNewPaymentForm
    ) {
      // Find default payment method, or use first one
      // Only consider methods this surface can actually charge. An Optix-migrated
      // member's DEFAULT is often a bank row, so without this filter we'd
      // auto-select an instrument whose row renders disabled — leaving the user
      // unable to deselect it and Pay submitting a bank id that dead-ends at
      // PENDING_SETTLEMENT_UNSUPPORTED. Disabling the row is not enough on its own.
      const selectable = savedPaymentMethods.filter(
        (pm) => !UNSELECTABLE_METHOD_TYPES.includes(pm.methodType ?? 'card')
      );
      const defaultMethod = selectable.find((pm) => pm.isDefault);
      const methodToSelect = defaultMethod || selectable[0];

      if (methodToSelect) {
        console.log(
          "[PaymentDetailsStep] Auto-selecting payment method:",
          methodToSelect.id,
          "isDefault:",
          methodToSelect.isDefault
        );
        setSelectedPaymentMethodId(methodToSelect.id.toString());
        setPaymentMethodToken(methodToSelect.id.toString(), paymentProvider);
      }
    }
  }, [
    isAuthenticated,
    savedPaymentMethods,
    selectedPaymentMethodId,
    showNewPaymentForm,
    paymentProvider,
    setPaymentMethodToken,
  ]);

  // Find selected bay from club data
  const selectedBay = useMemo(
    () => club?.bays?.find((bay) => bay.id === selectedBayId),
    [club?.bays, selectedBayId]
  );

  // Duration only. This step computes NO price: every figure comes off
  // `/bookings/validate` (member) or `/bookings/quote` (guest), the only readers
  // that can see a per-bay premium or the club's pricing mode. The local
  // calculation this replaced produced a different number in each unresolved
  // state — $0 when the query was idle, a premium-blind walk-in total while it
  // was in flight — and `isPriceUnknown` already covers both properly.
  const durationMinutes =
    selectedStartTime && selectedEndTime
      ? (timeStringToMinutes(selectedEndTime) || 0) - (timeStringToMinutes(selectedStartTime) || 0)
      : 0;

  // Helper: Convert club date/time to UTC ISO string for API
  const createUTCTimestamp = useMemo(() => {
    return (timeString: string): string | null => {
      if (!club?.timezone || !selectedDate) {
        return null;
      }

      const dateStr = selectedDate;

      try {
        // Use datetime library to properly convert club time to UTC
        const result = createClubDateTimeFromSlot(
          dateStr,
          timeString,
          club.timezone
        );
        return result.date.toISOString();
      } catch (error) {
        // Log error for monitoring and debugging
        console.error(
          "[PaymentDetailsStep] Failed to create UTC timestamp:",
          error
        );

        // Capture error with monitoring for alerting
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            level: "error",
            tags: {
              operation: "create_utc_timestamp",
              club_timezone: club.timezone,
            },
            extra: {
              dateStr,
              timeString,
              selectedDate: selectedDate,
            },
          }
        );

        // Re-throw to avoid silently converting an exceptional condition into a normal null value
        throw error;
      }
    };
  }, [selectedDate, club?.timezone]);

  // Build ISO timestamps for validation API
  const startTimeISO = useMemo(
    () => (selectedStartTime ? createUTCTimestamp(selectedStartTime) : null),
    [selectedStartTime, createUTCTimestamp]
  );

  const endTimeISO = useMemo(
    () => (selectedEndTime ? createUTCTimestamp(selectedEndTime) : null),
    [selectedEndTime, createUTCTimestamp]
  );

  // Get numeric user ID for validation
  const numericUserId = useMemo(() => {
    if (!authUser?.id) return undefined;
    return typeof authUser.id === "number"
      ? authUser.id
      : parseInt(authUser.id);
  }, [authUser?.id]);

  // Call booking validation API to get real subscription pricing — and, since D2,
  // a quote for non-members too (may include a per-bay premium the client cannot
  // price locally). The enable condition is named because it is also a quote state:
  // while the query is disabled React Query reports `isLoading: false` with no data,
  // which is indistinguishable from "answered" unless we track it explicitly.
  // No `isAuthenticated` here any more: a booker with no account is priced by
  // `POST /bookings/quote`, which `useBookingPricing` picks for them. The client
  // has no locally-computed price left to fall back on.
  const validationEnabled = !!selectedBayId && !!startTimeISO && !!endTimeISO;
  // One hook, both auth states: it calls /bookings/validate for a member and
  // /bookings/quote for a guest, and the difference stops here.
  const pricing = useBookingPricing({
    clubId: club?.id || null,
    bayId: selectedBayId,
    startTime: startTimeISO,
    endTime: endTimeISO,
    userId: numericUserId,
    guestCount,
    promoCode: promoCode ?? undefined,
    enabled: validationEnabled,
  });
  const { isLoading: isValidating, isError: validationFailed, refetch: refetchPricing } = pricing;
  // The canonical code the quote applied, or undefined. Off the SERVER's verdict,
  // and carrying the server's spelling of the code rather than what was typed.
  const promoCodeForPurchase = pricing.promotion?.applied ? pricing.promotion.code : undefined;
  // Shaped like the old validate payload so the rest of this component reads
  // unchanged; `usage`/`overage` are simply absent on the guest path.
  const validationData = pricing.hasQuote
    ? {
        isValid: pricing.isValid,
        errors: pricing.errors,
        fees: pricing.fees,
        totalCost: pricing.totalCost,
        /** Absent unless the booking divides across payers — see the pricing hook. */
        payerBreakdown: pricing.payerBreakdown,
        canProceedWithPayment: pricing.canProceedWithPayment,
        overage: pricing.overage,
        usage: pricing.usage,
        paymentTiming: pricing.paymentTiming,
        credit: pricing.credit,
        paymentMethodRequired: pricing.paymentMethodRequired,
      }
    : undefined;

  // Whether a plan pays for THIS booking. `hasActiveSubscription` is who the member
  // is; on a bay left out of plan coverage the quote answers `subscription: null`
  // and they are priced as a drop-in. Every price and payment decision below reads
  // this; identity copy and telemetry keep `hasActiveSubscription`.
  const planPays = planPaysHere({
    hasActiveSubscription,
    hasQuote: pricing.hasQuote,
    quoteSubscription: pricing.subscription,
  });

  // WHICH plan pays it. A member holding a membership and a pass pays with the pass
  // whenever it covers the whole booking (pass-first, subscription unification D13.2);
  // the quote answers as that payer, so coverage copy reads it — never the elected
  // plan, which stays the member's identity. Before a quote lands: the elected plan.
  const payer = payerFromQuote({
    quoteSubscription: pricing.hasQuote ? (pricing.subscription ?? null) : undefined,
    holdings,
    elected: activeSubscription,
  });
  const payerType = payer?.type;

  // Effective guest fee params. Subscribed users get the SERVER-resolved
  // policy (instance → template → club, disabled for teams) of the plan that
  // PAYS this booking — a pair holder's pass prices its own guests (pass-first,
  // subscription unification D13.5) — so the quote matches what booking
  // creation charges; walk-ins resolve club defaults
  // locally, matching the server's null-overrides path. The local resolve is
  // also the fallback under version skew (an older API omitting guestFeePolicy
  // — unreachable per the types, real during rollout), where it must carry the
  // subscription's own overrides so the degraded path is the pre-policy
  // behavior, not a worse club-defaults quote.
  const feePlan = payer?.card ?? activeSubscription;
  const resolvedGuestFeeParams = useMemo(() => {
    if (feePlan?.guestFeePolicy) return feePlan.guestFeePolicy;
    const plan = feePlan?.type === 'membership' ? feePlan.plan : null;
    return resolveGuestFeeParams(
      club?.guestFeesEnabled ?? false,
      club?.guestFeeConfig ?? { freeGuestsPerBooking: 0, feePerGuest: 0 },
      feePlan
        ? {
            guestFeeOverride: feePlan.guestFeeOverride ?? plan?.guestFeeOverride ?? null,
            freeGuestsOverride: feePlan.freeGuestsOverride ?? plan?.freeGuestsOverride ?? null,
          }
        : null,
    );
  }, [club, feePlan]);

  // Single source for the guest fee. Both price memos below and the navigation
  // state handed to the confirmation page read this, so a member cannot be quoted
  // one number and shown another on their receipt.
  const guestFee = useMemo(
    () =>
      calculateGuestFee(
        guestCount,
        resolvedGuestFeeParams.freeGuestsPerBooking,
        resolvedGuestFeeParams.feePerGuest,
      ),
    [guestCount, resolvedGuestFeeParams],
  );
  const guestFeeDollars = guestFee.guestFeeTotal / 100;


  // Overage wording, resolved once for the whole component — a session breach
  // is measured in slots, not minutes, and both the line items and the card
  // read from here so they cannot describe the same breach differently.
  const overageCopy = describeOverage({
    limitType: validationData?.overage?.limitType ?? 'active',
    coveredMinutes: validationData?.overage?.coveredMinutes ?? durationMinutes,
    overageMinutes: validationData?.overage?.overageMinutes ?? 0,
    planNoun: payer?.type === 'pass' ? 'pass' : 'membership',
  });

  // A server quote landed for a booker no plan pays for — guest, signed-in
  // non-member, or a member on an uncovered bay, all priced identically. The local
  // memo above survives only as a muted skeleton while this is false; it must never
  // feed a submitted amount.
  const hasNonMemberQuote = !planPays && !!validationData?.fees?.length;
  // The server refused this booking outright — subscription-only club, bay gate,
  // or a pricing failure — rather than pricing it. `canProceedWithPayment` is only
  // set `false` on the pricing-failure shape; absent means the plain refusal
  // shape, so negating it catches both.
  const validationRefused = validationData?.isValid === false && !validationData?.canProceedWithPayment;
  // Server-quoted or empty — there is no local estimate to fall back to. The card
  // is not rendered at all while `isPriceUnknown`, so the zeroed shape is never
  // shown as a price.
  const priceSummary: BookingPriceSummary = hasNonMemberQuote && validationData
    ? {
        lineItems: validationData.fees.map((f) => ({
          key: f.type,
          label: f.label,
          amountDollars: f.baseAmount,
        })),
        subtotalDollars: validationData.totalCost?.baseAmount ?? 0,
        taxRatePercent: validationData.totalCost?.taxRatePercent ?? 0,
        taxDollars: validationData.totalCost?.taxAmount ?? 0,
        totalDollars: validationData.totalCost?.total ?? 0,
      }
    : { lineItems: [], subtotalDollars: 0, taxRatePercent: 0, taxDollars: 0, totalDollars: 0 };

  // Convenience aliases for backward compat with the rest of the component
  const subtotal = priceSummary.subtotalDollars;
  const tax = priceSummary.taxDollars;
  const total = priceSummary.totalDollars;

  // Card processing-fee passthrough — read off the quote, which is already
  // fee-inclusive (the server quote sizes the fee), so the total is never
  // double-counted. The guest-fee branch below still sizes a fee locally; that
  // one is a member's own line and does not depend on the club's pricing mode.
  const walkInProcessingFee = validationData?.totalCost?.processingFee ?? 0;
  const processingFeeRate = validationData?.totalCost?.processingFeeRate ?? 0;
  const walkInTotal = total;

  // Get tax rate for display (already in percentage form)
  const taxRate = getAppliedTaxRate(club);

  // Format duration for display
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours} Hour ${mins} Min`;
    if (hours > 0) return `${hours} Hour${hours > 1 ? "s" : ""}`;
    return `${mins} Minutes`;
  };

  // The guest fee AMOUNT, as the server priced it. Only the label's
  // `chargedGuests` still comes from the local resolution below, because the
  // server does not send that count.
  //
  // The split matters: every total on this card reads `validationData.totalCost`,
  // so sourcing this row locally would let a card's line items and its total
  // disagree — a summary that visibly does not add up, which is the exact failure
  // the server quote exists to prevent. It also reaches the confirmation
  // receipt, which must show the figure that was charged.
  const quotedGuestFee =
    validationData?.fees.find((f) => f.type === 'guest_fee')?.baseAmount ?? 0;

  // Calculate subscription pricing from validation API response
  const subscriptionPricing = useMemo(() => {
    // Guest fee (common across subscription cases) — presence from the local
    // resolution, amount from the quote.
    const hasGuestFee = guestFeeDollars > 0;

    const guestFeeLineItem: BookingLineItem = {
      key: 'guestFee',
      label: `Guest fees (${formatGuestFeeLabel(guestFee.chargedGuests, guestFee.guestFeeTotal)})`,
      amountDollars: quotedGuestFee,
    };

    // No plan pays for this booking (none held, or this bay is not covered)
    if (!planPays || !activeSubscription) {
      return {
        overageCharge: 0,
        overageTax: 0,
        overageTotal: 0,
        finalTotal: total,
        processingFee: 0,
        isCovered: false,
        hasOverage: false,
        isValidating: false,
        lineItems: [] as BookingLineItem[],
      };
    }

    // If still validating, return placeholder with loading flag
    if (isValidating) {
      return {
        overageCharge: 0,
        overageTax: 0,
        overageTotal: 0,
        finalTotal: total,
        processingFee: 0,
        isCovered: false,
        hasOverage: false,
        isValidating: true,
        lineItems: [] as BookingLineItem[],
      };
    }

    // If validation data available, use it
    if (validationData) {
      const hasOverage = validationData.overage?.hasOverage || false;

      // Extract overage fee from fees[] array (replaces overageCost)
      const overageFee = validationData.fees.find(f => f.type === 'overage');
      const overageBaseAmount = overageFee?.baseAmount || 0;


      const membershipCoverageItem: BookingLineItem = {
        key: 'membershipCoverage',
        // The paying plan's noun — a pass-only holder used to read "Membership Coverage" too.
        label: payerType === 'pass' ? 'Pass Coverage' : 'Membership Coverage',
        subtitle: overageCopy.coveredSubtitle,
        amountDollars: 0,
        displayAmount: overageCopy.coveredValueLabel,
        highlight: true,
      };

      if (!hasOverage && !hasGuestFee) {
        // No overage, no guest fee — fully covered
        return {
          overageCharge: 0,
          overageTax: 0,
          overageTotal: 0,
          finalTotal: 0,
          processingFee: 0,
          isCovered: true,
          hasOverage: false,
          isValidating: false,
          lineItems: [membershipCoverageItem],
        };
      }

      if (!hasOverage && hasGuestFee) {
        // Covered booking, guests to pay for. Read the server's totals like the two
        // branches below — this used to compute tax and the processing fee locally
        // over `guestFeeDollars`. It agreed with the server (the client fee gate
        // resolves the HOME club, same as `resolveHomeClubFeeGate`, and this app
        // books at its own club so the tax base matches), but agreeing by
        // construction is not the same as having one source, and `finalTotal`
        // feeds `deriveBookingQuote` → whether this member is asked for a card.
        //
        // `totalCost` is present whenever a guest fee is actually owed —
        // `assembleFeeItems` builds `guest_fee` for plan holders too, so the
        // subtotal exceeds zero. It is absent for a TEAM booking, where the fee is
        // waived and `0` is the correct answer.
        const guestFeeTotal = validationData.totalCost?.total ?? 0;
        return {
          overageCharge: 0,
          overageTax: validationData.totalCost?.taxAmount ?? 0,
          overageTotal: guestFeeTotal,
          finalTotal: guestFeeTotal,
          processingFee: validationData.totalCost?.processingFee ?? 0,
          isCovered: false,
          hasOverage: false,
          isValidating: false,
          lineItems: [membershipCoverageItem, guestFeeLineItem],
        };
      }

      // Has overage
      const overageLineItem: BookingLineItem = {
        key: 'overage',
        label: 'Overage Charges',
        subtitle: overageCopy.overageSubtitle,
        amountDollars: overageBaseAmount,
        highlight: true,
      };

      if (!hasGuestFee) {
        // Overage only — use API totalCost (already includes tax + the processing fee, so
        // read the fee off the server response rather than recomputing → no double-count).
        const overageTotal = validationData.totalCost?.total || 0;
        const overageTax = validationData.totalCost?.taxAmount || 0;
        return {
          overageCharge: overageBaseAmount,
          overageTax,
          overageTotal,
          finalTotal: overageTotal,
          processingFee: validationData.totalCost?.processingFee || 0,
          isCovered: false,
          hasOverage: true,
          isValidating: false,
          lineItems: [membershipCoverageItem, overageLineItem],
        };
      }

      // Overage + guest fee — API totalCost (combined tax + fee already calculated)
      const combinedTotal = validationData.totalCost?.total || 0;
      const combinedTax = validationData.totalCost?.taxAmount || 0;
      return {
        overageCharge: overageBaseAmount,
        overageTax: combinedTax,
        overageTotal: combinedTotal,
        finalTotal: combinedTotal,
        processingFee: validationData.totalCost?.processingFee || 0,
        isCovered: false,
        hasOverage: true,
        isValidating: false,
        lineItems: [membershipCoverageItem, overageLineItem, guestFeeLineItem],
      };
    }

    // Fallback: assume covered (safe default)
    return {
      overageCharge: 0,
      overageTax: 0,
      overageTotal: 0,
      finalTotal: 0,
      processingFee: 0,
      isCovered: true,
      hasOverage: false,
      isValidating: false,
      lineItems: [] as BookingLineItem[],
    };
  }, [
    planPays,
    payerType,
    activeSubscription,
    validationData,
    quotedGuestFee,
    isValidating,
    durationMinutes,
    total,
    taxRate,
    guestFee,
    guestFeeDollars,
  ]);

  // ── The money decision ─────────────────────────────────────────────────────
  // Read the requirement, never `finalTotal` directly. The memo above always
  // produces a number, including for states where we simply do not know the price
  // yet — and every one of those numbers used to be treated as real.
  const bookingQuote = deriveBookingQuote({
    // A guest runs no subscription query, so they are settled by definition.
    subscriptionSettled: isAuthenticated ? subscriptionSettled : true,
    validationEnabled,
    isValidating,
    validationFailed,
    hasValidationData: !!validationData,
    // For a plan holder this is the subscription memo's total; for everyone else
    // it is the server quote's, which is now the only price they have.
    //
    // Except when the server says the booking divides: on a team booking the
    // overage is charged to the team's leads, and what decides whether to ask
    // THIS member for a card is their own share. `payerBreakdown.booker` being
    // absent is the server saying they owe nothing — so a member with no card
    // on file can book past the pool, which is the point of the feature.
    finalTotal: validationData?.payerBreakdown
      ? (validationData.payerBreakdown.booker?.total ?? 0)
      : planPays ? subscriptionPricing.finalTotal : walkInTotal,
    validationRefused,
    paymentTiming: validationData?.paymentTiming,
    paymentMethodRequired: validationData?.paymentMethodRequired,
  });
  const paymentRequirement = resolvePaymentRequirement(bookingQuote);
  const isPriceUnknown = paymentRequirement === 'unknown';
  // The club collects what is owed at the counter: no card, and the total reads "Due at Club".
  const isPayAtClub = paymentRequirement === 'at-club';
  // A signed-in member with nothing for a card to pay: nothing is owed, or their account
  // credit covers it (credits X6). Book with no card — the server settles from credit.
  const isCreditCovered = paymentRequirement === 'credit-covered';
  const isNoCardNeeded = isAuthenticated && (paymentRequirement === 'not-required' || isCreditCovered);
  // The page header reads "Review and confirm your booking" while no card is asked for here.
  const needsNoCard = isNoCardNeeded || isPayAtClub;
  useEffect(() => {
    setPaymentNeedsNoCard(needsNoCard);
  }, [needsNoCard, setPaymentNeedsNoCard]);
  useEffect(() => () => setPaymentNeedsNoCard(false), [setPaymentNeedsNoCard]);

  const handleAddPaymentMethod = async () => {
    try {
      setIsProcessing(true);

      // Validate club is loaded
      if (!club?.id) {
        butterToast.error('Club information not loaded. Please refresh the page.');
        setIsProcessing(false);
        return;
      }

      // Call /users/incomplete to create or find user with Supabase account
      const result = await userService.createOrFindIncompleteUser({
        email: state.email.toLowerCase().trim(),
        clubId: club.id.toString(),
        source: 'booking',
      });

      console.log("[PaymentDetailsStep] Incomplete user result:", result);

      // Show success message if provided by API
      if (result.message) {
        console.log("[PaymentDetailsStep] API message:", result.message);
      }

      // Store user in BookingContext (access via result.payload)
      setGuestUser(result.payload.user, result.payload.isExisting);

      /**
       * `pending` is deliberately bundled with `incomplete`: both mean "account row
       * exists, user-chosen password never set" (both were provisioned with
       * `randomPlaceholderPassword`), so both must continue to CompleteAccountForm
       * rather than the sign-in modal. A `pending` member literally cannot
       * authenticate, so showing them a password prompt is a dead end.
       *
       * ⚠️ The form this routes to calls `completeAccountSetup` WITHOUT a setupToken.
       * For `pending` that only works while the TEMPORARY G23 window is open in the API
       * (completeAccountSetup step 2b). When G23 is reverted, `pending` members will
       * fail there with "A valid setup link is required to finish setting up your
       * account" and dead-end mid-booking.
       *
       * Do NOT "fix" that by dropping `pending` from this branch — that sends them to a
       * sign-in they can never pass. The fix is a token this flow can actually obtain:
       * /apps/api/src/services/auth/docs/rate-limiting-plan.md.
       *
       * Mirrored at SubscriptionReviewStep.tsx and both FinishAccountStep.tsx files.
       * @see /apps/api/src/services/auth/docs/KNOWN_GAPS.md G23, G8
       */
      const isIncompleteUser = needsAccountSetup(result.payload.user);

      if (result.payload.isExisting && !isIncompleteUser) {
        // Existing user with password - show sign-in modal
        console.log(
          "[PaymentDetailsStep] Existing user with active status, showing sign-in modal"
        );
        setPaymentStep("sign-in");
      } else {
        // New user OR incomplete user - proceed to payment
        // Incomplete users will complete their account after payment
        console.log(
          "[PaymentDetailsStep] New user or incomplete user, proceeding to payment step"
        );
        setPaymentStep("payment");
      }
    } catch (err) {
      console.error("[PaymentDetailsStep] Failed to check user:", err);
      butterToast.error(
        err instanceof Error
          ? err.message
          : "Failed to process request. Please try again."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle successful sign-in
  const handleSignInSuccess = () => {
    console.log("[PaymentDetailsStep] Sign-in successful");

    // The auth context will be updated automatically via AuthProvider
    // Payment methods will be loaded via the usePaymentMethodsQuery hook
    // which is enabled when isAuthenticated becomes true

    // Proceed to payment step where saved methods will be shown
    setPaymentStep("payment");
  };

  // Handle selecting an existing saved payment method
  const handleSelectPaymentMethod = (pmId: string) => {
    console.log("[PaymentDetailsStep] Selected existing payment method:", pmId);

    // Track payment method selection
    addBreadcrumb(
      "payment",
      "User selected saved payment method",
      {
        paymentMethodId: pmId,
        hasActiveSubscription,
        hasOverage: subscriptionPricing.hasOverage,
      },
      "info"
    );

    setSelectedPaymentMethodId(pmId);

    // Ensure guestUser is set from authenticated user for auto-advance logic
    if (isAuthenticated && authUser && !guestUser) {
      setGuestUser(authUser, true);
    }

    // Store the payment method ID in booking context
    // For existing saved methods, we store the database ID directly
    setPaymentMethodToken(pmId, paymentProvider);
  };

  // Handle "Add new payment method" click
  const handleAddNewPaymentMethod = () => {
    console.log("[PaymentDetailsStep] User wants to add new payment method");
    console.log(
      "[PaymentDetailsStep] Current state - isAuthenticated:",
      isAuthenticated,
      "authUser:",
      authUser,
      "guestUser:",
      guestUser
    );

    // Ensure guestUser is set from authenticated user for payment processing
    if (isAuthenticated && authUser && !guestUser) {
      console.log(
        "[PaymentDetailsStep] Setting guestUser from authUser for new payment method"
      );
      setGuestUser(authUser, true);
    } else if (isAuthenticated && authUser && guestUser) {
      console.log(
        "[PaymentDetailsStep] guestUser already set, no action needed"
      );
    }

    setShowNewPaymentForm(true);
    setSelectedPaymentMethodId(null);
    setPaymentMethodToken(null, null);
  };

  // Handle payment method saved
  const handlePaymentSuccess = async (providerToken: string) => {
    console.log(
      "[PaymentDetailsStep] Payment token received from provider:",
      providerToken
    );
    console.log("[PaymentDetailsStep] Payment provider:", paymentProvider);

    try {
      setIsProcessing(true);

      // No waiver check here. By the time a provider token arrives the single-use
      // SetupIntent is already spent, so refusing now strands a card attached at
      // Stripe but absent from our DB. The checkout button checks the waiver
      // before it submits the card form (handleCtaClick) and is disabled until it
      // passes; the account step re-checks it for guests before booking.

      // Ensure guestUser is set from authenticated user if not already set
      if (isAuthenticated && authUser && !guestUser) {
        console.log(
          "[PaymentDetailsStep] Setting guestUser from authUser in handlePaymentSuccess"
        );
        setGuestUser(authUser, true);
      }

      // Get user ID - prefer authenticated user, fallback to guest user
      const userId = authUser?.id || guestUser?.id;

      // Validate required data
      if (!userId) {
        throw new Error(
          "User ID not found. Please restart the booking process."
        );
      }
      if (!club?.id) {
        throw new Error("Club ID not found. Please refresh the page.");
      }
      if (!paymentProvider) {
        throw new Error("Payment provider not detected.");
      }

      console.log("[PaymentDetailsStep] Saving payment method to database...");

      // Persist the card (Stripe confirm-setup-intent or Square token) and get
      // its database id — the shared primitive handles the retry-on-confirm-failure.
      const databaseId = await saveCard(providerToken);

      console.log(
        "[PaymentDetailsStep] Payment method saved with database ID:",
        databaseId
      );

      const savedId = databaseId.toString();

      if (isAuthenticated) {
        // Members: one click saves AND books. Show the saved list with the new
        // card selected, then book with it — passed explicitly, because this
        // render's `selectedPaymentMethodId` is still null.
        setShowNewPaymentForm(false);

        // A bank account can't be charged until ACH settlement ships (it dead-ends
        // at PENDING_SETTLEMENT_UNSUPPORTED). Keep it saved, but don't select it or
        // book with it. Unreachable while ACH setup is switched off server-side.
        if (newMethodKind === "bank") {
          butterToast.error(
            "Bank accounts can't be used for payment yet. Choose a card to complete your booking."
          );
          setIsProcessing(false);
          return;
        }

        setSelectedPaymentMethodId(savedId);
        setPaymentMethodToken(savedId, paymentProvider);

        // Fire-and-forget: resolving tells the card form "saved", which is now
        // true. A booking failure must not throw back into the form (it would
        // render as a card-save error); handleConfirmBooking reports its own, and
        // the same button then retries the booking with the card already selected.
        void handleConfirmBooking({ paymentMethodId: savedId });
        return;
      }

      // Guests: store the DATABASE id (not the provider token) and let
      // useAutoAdvanceAfterSave take them to account setup.
      setPaymentMethodToken(savedId, paymentProvider);
      setPaymentSaved(true);
    } catch (err) {
      console.error("[PaymentDetailsStep] Failed to save payment method:", err);
      // Broad client-side capture: reaches here on a client-state guard (missing
      // user/club/provider), the post-`/confirm` `!databaseId` response anomaly,
      // or a re-thrown persist failure. Tagged for filtering; the provider SDK
      // failures were already captured inside the card form.
      reportPaymentClientError("save_card", err, {
        kind: "client_flow",
        surface: "web_booking",
        extra: { provider: paymentProvider },
      });
      butterToast.error(
        err instanceof Error
          ? err.message
          : "Failed to save payment method. Please try again."
      );
      setIsProcessing(false);
      // Reset payment saved flag to prevent auto-advance on error
      setPaymentSaved(false);
      // Clear any potentially stale payment method token from booking context
      setPaymentMethodToken(null, null);
    }
  };

  // Handle confirm booking for authenticated users. `paymentMethodId` is passed
  // by the one-click chain, whose render has not seen the new card selected yet.
  // An object, not a bare string, so an onClick can't hand it a MouseEvent.
  const handleConfirmBooking = async ({ paymentMethodId }: { paymentMethodId?: string } = {}) => {
    try {
      setIsProcessing(true);

      // Add breadcrumb for booking confirmation start
      addBreadcrumb(
        "booking",
        "User clicked Confirm Booking",
        {
          hasOverage: subscriptionPricing.hasOverage,
          overageAmount: subscriptionPricing.overageTotal,
          hasActiveSubscription,
          subscriptionType: activeSubscription?.type,
        },
        "info"
      );

      // Validate all required data
      if (!authUser) {
        throw new Error("User not found. Please log in again.");
      }
      if (
        !selectedDate ||
        !selectedStartTime ||
        !selectedEndTime ||
        !selectedBayId
      ) {
        throw new Error(
          "Missing booking selections. Please complete all steps."
        );
      }
      if (!startTimeISO || !endTimeISO) {
        throw new Error("Invalid booking time. Please refresh and try again.");
      }
      if (!club) {
        throw new Error(
          "Club information not loaded. Please refresh the page."
        );
      }

      // Get payment method ID - the just-saved card, else the selected one. None when the
      // club collects at the counter: the server writes a due invoice and charges no card.
      const pmId =
        isPayAtClub || isNoCardNeeded ? undefined : paymentMethodId ?? (selectedPaymentMethodId || paymentMethodToken);

      // Never submit against a price we do not have. This is the state that used to
      // read as "covered": the flow would send no charge decision at all while the
      // server priced the booking itself and billed the auto-selected default card.
      if (isPriceUnknown) {
        throw new Error(
          "We're still confirming the price for this booking. Please wait a moment and try again."
        );
      }

      if (paymentRequirement === 'required' && !pmId) {
        throw new Error(
          "Payment method not found. Please select or add a payment method."
        );
      }

      // Sign BEFORE the booking is created. The alternative — book first, sign
      // after — leaves a booking against an unsigned waiver whenever the second
      // call fails, which is precisely the record a club cannot have.
      //
      // `satisfied` is vacuously true when nothing is outstanding (the entry
      // gate already collected it), and `submitWaivers` no-ops when nothing was
      // captured — so this costs nothing in the common case.
      if (!waiverGate.satisfied) {
        throw new Error(waiverRefusalMessage(waiverGate.answerMissing));
      }
      await waiverGate.submitWaivers();

      console.log(
        "[PaymentDetailsStep] Creating booking for authenticated user..."
      );

      // Convert userId to number
      const userId = toNumericUserId(authUser.id);

      // Build booking request. No `paymentType`: the server elects the plan
      // that pays from the member's holdings and strips any client claim.
      const bookingRequest = {
        clubId: club.id,
        bayId: selectedBayId,
        userId,
        startTime: startTimeISO,
        endTime: endTimeISO,
        guestCount,
        // finalTotal correctly maps to:
        //   walk-in (no subscription) → total (full price incl. tax)
        //   subscription, no overage  → 0 (fully covered)
        //   subscription, with overage → overageTotal
        paymentAmount: subscriptionPricing.finalTotal,
        // Echo the /validate quote back for server reconciliation — overage/guest
        // fees for members, the full breakdown (incl. any bay premium) for non-members.
        quotedFees: toQuotedFees(validationData?.fees),
        // And the timing it promised, so a club that changed how it takes payment since
        // refuses rather than charging a card the member was told they did not need.
        ...(validationData?.paymentTiming ? { quotedPaymentTiming: validationData.paymentTiming } : {}),
        // The purchase only ever sends a code the QUOTE applied — the rule both
        // subscription funnels follow. A typed-then-refused code costs nothing
        // at Confirm, and it is what lets the server treat ANY create-time
        // refusal as "this died between the price you saw and this click" and
        // refuse rather than book you at list.
        ...(promoCodeForPurchase ? { promoCode: promoCodeForPurchase } : {}),
      } as {
        clubId: number;
        bayId: number;
        userId: number;
        startTime: string;
        endTime: string;
        paymentAmount: number;
        guestCount?: number;
        paymentMethodId?: string;
        hasOveragePayment?: boolean;
        overagePayment?: {
          overageMinutes: number;
          overageAmount: number;
          paymentMethodId: string;
        };
        quotedFees?: QuotedFee[];
        quotedPaymentTiming?: 'now' | 'at_club';
        promoCode?: string;
      };

      // Send the chosen card whenever we have one. The server only charges when
      // `isPaymentRequired` says so, so an id on a covered booking is inert —
      // whereas a MISSING id on a chargeable one makes the server fall back to the
      // member's default card (resolveDefaultMethod), charging them silently.
      if (pmId) {
        bookingRequest.paymentMethodId = pmId;
      }

      // Add overage payment if applicable
      if (
        subscriptionPricing.hasOverage &&
        validationData?.overage &&
        validationData?.totalCost &&
        pmId
      ) {
        bookingRequest.hasOveragePayment = true;
        bookingRequest.overagePayment = {
          overageMinutes: validationData.overage.overageMinutes,
          // totalCost.total is the combined charge (overage + any guest fees).
          // The API orchestrator ignores this value for payment — it recalculates
          // all fees server-side. This field is used only for informational logging.
          overageAmount: validationData.totalCost.total,
          paymentMethodId: pmId,
        };
      }

      // Create booking (navigation happens in onSuccess)
      createBooking.mutate(bookingRequest);
    } catch (err) {
      console.error("[PaymentDetailsStep] Failed to create booking:", err);
      // Pre-mutate client guards (missing selections / invalid time / no club).
      // The createBooking mutation failure itself is captured in its onError.
      reportPaymentClientError("confirm_booking_guards", err, {
        kind: "client_flow",
        level: "warning",
        surface: "web_booking",
      });
      butterToast.error(
        err instanceof Error
          ? err.message
          : "Failed to create booking. Please try again."
      );
      setIsProcessing(false);
    }
  };

  // Handle back to bay selection
  const handleBackToBaySelection = () => {
    setVisualStep(VISUAL_STEPS.BOOKING_TIME);
    goToPreviousStep();
  };

  // The single checkout button — see checkoutCta.ts for every state it can be in.
  const cta = resolveCheckoutCta({
    isAuthenticated,
    isLoadingPaymentMethods: isAuthenticated && isLoadingPaymentMethods,
    hasSelectedMethod: selectedPaymentMethodId != null,
    showingNewPaymentForm,
    cardReady,
    waiverSatisfied: waiverGate.satisfied,
    isPriceUnknown,
    hasOverage: subscriptionPricing.hasOverage,
    isPayAtClub,
    isNoCardNeeded,
    busy,
  });

  // The button is disabled in these states already; this re-checks at the moment
  // of the click, BEFORE a `submit-card` lets the native submit reach the card
  // form — after that, the single-use SetupIntent is spent.
  const handleCtaClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!waiverGate.satisfied) {
      event.preventDefault();
      butterToast.error(waiverRefusalMessage(waiverGate.answerMissing));
      return;
    }
    if (isAuthenticated && isPriceUnknown) {
      event.preventDefault();
      butterToast.error(
        "We're still confirming the price for this booking. Please wait a moment and try again."
      );
      return;
    }
    // Members only: a guest's "Continue" saves a card; their booking is
    // confirmed on the account step, not here. Holding the click also stops the
    // native form= submit, so a cancel doesn't spend the single-use SetupIntent;
    // "Yes, Continue" clicks the CTA again and every check above re-runs.
    if (isAuthenticated && earlyConfirm.interceptClick(event)) return;
    if (cta.action === "confirm-saved" || cta.action === "confirm-no-card") void handleConfirmBooking();
    // A guest owes the club, not a card: mark it and let the auto-advance take them on.
    if (cta.action === "continue-no-card") {
      setPayAtClub(true);
      setPaymentSaved(true);
    }
    // 'submit-card': the native `form=` submit goes ahead.
  };

  return (
    <div className="">
      {/* Back to Bay Selection Link — locked while a save or booking is in flight */}
      <button
        onClick={handleBackToBaySelection}
        disabled={busy}
        className="inline-flex items-center gap-2 text-sm text-fg-quaternary text-interactive:text-fg-tertiary transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        data-testid="checkout-back-to-bay-selection-btn"
      >
        <ArrowIcon className="h-4 w-4 rotate-180" />
        Back to Bay Selection
      </button>

      {/* Two-column layout on desktop.
          No `order-*` anywhere: the DOM order below (Booking, then Membership +
          Price, then Payment) is already the mobile order the design asks for, so
          a single-column layout reads correctly on its own. Reordering visually is
          what pushed the breakdown — including the guest-fee row — below the
          payment card and off the bottom of a phone screen.
          Desktop is two plain columns, per the design: Booking Details, Membership
          and Price on the left; Payment, the waiver and the checkout button on the
          right. Column wrappers rather than row/col placement, so a tall right
          column (the waiver) can't stretch a grid row and open a gap on the left. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-start gap-8 mt-10">
        {/* Left — Booking Details, then Membership + Price. First on mobile. */}
        <div className="space-y-6">
          <BookingDetailsCard
            imageUrl={selectedBay?.image?.url || null}
            imageAlt={selectedBay?.image?.alt}
            name={selectedBay?.name || "Bay"}
            bayId={selectedBayId || 0}
            date={format(parseISO(selectedDate), "EEEE, d MMMM, yyyy")}
            time={formatTimeRange(
              selectedStartTime || "00:00",
              selectedEndTime || "00:00"
            )}
            duration={formatDuration(durationMinutes)}
          />

          {/* Subscription Details (if the member's plan pays for this booking) */}
          {planPays && activeSubscription && (
            <SubscriptionDetailsCard
              type={payer?.type ?? activeSubscription.type}
              name={payer?.name ?? subscriptionName ?? ''}
            />
          )}

          {/* Overage Summary (if subscription has overage).
              Hidden once the server has REFUSED the booking — on a team booking,
              because nobody can be charged for the overage. The refusal banner
              below already says so, and this card sitting above it would be
              announcing the charge it just said cannot be made. */}
          {planPays &&
            subscriptionPricing.hasOverage &&
            validationData &&
            !validationRefused &&
            activeSubscription && (
              <OverageDetailsCard
                shared={isTeamBooking}
                message={
                  validationData.overage
                    ? overageCopy.explanation
                    : "Your date and time exceeds your subscription limits."
                }
                bookingDurationMinutes={durationMinutes}
                usage={validationData.usage ?? undefined}
                overage={validationData.overage ?? undefined}
                subscriptionName={payer?.name ?? subscriptionName ?? ''}
                data-testid="checkout-aditional-payment-message"
              />
            )}

          {/* Directly above the price card, the same place the subscription
              funnel puts it. The disclosure owns no capture of its own — the
              booking flow holds the code as step state. */}
          <PromoCodeDisclosure
            promoCode={promoCode}
            applied={
              pricing.promotion?.applied
                ? { code: pricing.promotion.code, label: pricing.promotion.label }
                : null
            }
            refusal={
              pricing.promotion && !pricing.promotion.applied
                ? {
                    reason: pricing.promotion.reason,
                    message:
                      pricing.promotion.message ?? 'This promo code could not be applied.',
                  }
                : null
            }
            onApply={(code) => {
              setPromoDropped(null);
              setPromoCode(code);
            }}
            onRemove={() => {
              setPromoDropped(null);
              setPromoCode(null);
            }}
          />

          {/* The auto-drop, said out loud. Without it the member clicks pay,
              nothing happens, and the total silently goes back up. */}
          {promoDropped && (
            <div
              role="status"
              data-testid="booking-promo-dropped"
              data-kind="promotion_rejected"
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
              {promoDropped} The code was removed — you can complete your booking at the
              regular price.
            </div>
          )}

          {/* Price Summary */}
          {/* The server could not price this booking. A disabled button with no
              explanation reads as a broken site on a checkout screen, so say what
              happened and offer the retry — submit stays blocked either way. */}
          {validationFailed && !validationRefused && (
            <RetryBanner
              message="We couldn't get a price for this booking."
              onRetry={() => refetchPricing()}
              testId="payment-quote-failed"
            />
          )}
          {validationRefused && validationData?.errors?.length ? (
            // The server refused the booking outright — there is no price to show,
            // only its own reason. Takes priority over the generic pending state.
            <div
              className="rounded-lg border border-red-200 bg-red-50 p-4"
              role="alert"
              data-testid="payment-validation-refused"
            >
              {validationData.errors.map((err, i) => (
                <p key={i} className="text-sm text-red-800">{err}</p>
              ))}
            </div>
          ) : (
          // `pending` is the same dashed card `FinishAccountStep` shows, so one
          // funnel has one unpriced state. It replaces a bespoke "Calculating your
          // price…" panel; the props below are still passed while pending and are
          // simply not read, which keeps this branch free of a second shape.
          <PriceSummaryCard
            pending={isPriceUnknown}
            totalLabel={isPayAtClub ? 'Due at Club' : undefined}
            // The member's account credit below the total (credits D9). The server sends it
            // only when they pay now, so never alongside "Due at Club".
            credit={validationData?.credit}
            subtotal={subtotal}
            tax={planPays ? subscriptionPricing.overageTax : tax}
            total={
              planPays ? subscriptionPricing.finalTotal : walkInTotal
            }
            taxRatePercent={
              // Pair the rate with the amount's source, exactly as processingFeeRate
              // does below: `tax` is the server's figure whenever a quote landed, so
              // labelling it with the client's club rate can print a percentage that
              // does not explain the number beside it.
              validationData?.totalCost?.taxRatePercent ?? taxRate
            }
            processingFee={
              planPays ? subscriptionPricing.processingFee : walkInProcessingFee
            }
            processingFeeRate={
              // Pair the rate with the amount's source: on the subscription/overage path the
              // amount is the server-quoted fee, so label it with the server rate (not the client
              // walk-in const). Falls back to the client rate when the server omits it.
              planPays
                ? (validationData?.totalCost?.processingFeeRate ?? processingFeeRate)
                : processingFeeRate
            }
            lineItems={
              planPays
                ? subscriptionPricing.lineItems
                : priceSummary.lineItems
            }
            showSubtotal={planPays}
            // The server already netted every fee, so this row is disclosure —
            // it must not be subtracted again. `discountTotal` covers both a
            // club's automatic overage discount and an applied promo code,
            // because what the member owes is one number either way.
            subscriptionDiscount={validationData?.totalCost?.discountTotal}
            discountLabel={validationData?.totalCost?.discountLabel}
          />
          )}
        </div>

        {/* Right — Payment, the waiver and the checkout button. Last on mobile.
            The booking card is in the left column, so it is deliberately not
            repeated here. */}
        <div className="space-y-8">
          {/* Payment Flow - Conditional Rendering Based on Step */}

          {/* STEP 1: Email Entry */}
          {paymentStep === "email" && (
            <EmailPaymentCard
              email={state.email}
              onEmailChange={setEmail}
              onAddPaymentMethod={handleAddPaymentMethod}
              isProcessing={isProcessing}
              actionLabel={isPayAtClub ? 'Continue' : undefined}
            />
          )}

          {/* STEP 2: Payment Method Capture */}
          {paymentStep === "payment" && (
            <CheckoutSection
              title="Payment Details"
              divider
              headingTestId="checkout-payment-details-title"
            >
              {/* Only show email input for unauthenticated users */}
              {!isAuthenticated && (
                <div>
                  <label htmlFor="checkout-email-locked" className="mb-2 block text-sm font-medium text-fg-quaternary">
                    Email
                  </label>
                  <input
                    id="checkout-email-locked"
                    type="email"
                    value={state.email}
                    disabled
                    className="w-full rounded-2xl border border-theme-600 bg-theme-50 px-4 py-3 text-base text-fg-primary disabled:cursor-not-allowed"
                    data-testid="checkout-email-locked"
                  />
                </div>
              )}

              {/* While saved methods are still loading, render nothing card-related —
                  otherwise the add-card form flash-mounts (savedPaymentMethods is []
                  mid-load), then unmounts when the query resolves and a saved card
                  auto-selects, racing the Square/Stripe element attach. */}
              {isPayAtClub ? (
                <div
                  className="rounded-2xl border border-theme-600 bg-theme-50 px-4 py-3"
                  data-testid="checkout-due-at-club"
                >
                  <p className="text-base font-medium text-fg-primary">Due at Club</p>
                  <p className="text-sm text-fg-quaternary">
                    You'll pay at the club. No card is needed to book.
                  </p>
                </div>
              ) : isNoCardNeeded ? (
                <div
                  className="flex items-start gap-3 rounded-2xl border border-theme-600 bg-theme-50 px-4 py-3"
                  data-testid="checkout-no-card-needed"
                >
                  <CheckmarkIcon className="mt-0.5 size-5 shrink-0 text-green-600" aria-hidden="true" />
                  <p className="text-base font-medium text-fg-primary">
                    {isCreditCovered
                      ? 'Paid in full with account credit — no card needed'
                      : 'No payment needed'}
                  </p>
                </div>
              ) : isAuthenticated && isLoadingPaymentMethods ? (
                <p className="text-sm text-gray-500">Loading payment methods…</p>
              ) : !showingNewPaymentForm ? (
                <PaymentMethodSelector
                  // Bank methods can't be charged until the ACH settlement flow
                  // lands — selecting one dead-ends at the confirm button with
                  // PENDING_SETTLEMENT_UNSUPPORTED. Remove in Phase 3.
                  unselectableMethodTypes={UNSELECTABLE_METHOD_TYPES}
                  unselectableReason="Bank accounts can't be used for payment yet"
                  paymentMethods={savedPaymentMethods}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  onSelect={handleSelectPaymentMethod}
                  onAddNew={handleAddNewPaymentMethod}
                  isProcessing={busy}
                />
              ) : (
                /* Show payment form for new users or when "add new" is clicked */
                <>
                  <CardEntryForm
                    paymentProvider={paymentProvider}
                    stripeSetupIntent={stripeSetupIntent}
                    stripeSetupIntentLoading={stripeSetupIntentLoading}
                    stripeSetupIntentError={stripeSetupIntentError}
                    retryStripeSetupIntent={retryStripeSetupIntent}
                    paymentSettings={effectivePaymentSettings}
                    onCardSuccess={handlePaymentSuccess}
                    errorLabel="PaymentDetailsStep"
                    // Without these a confirm failure on the highest-traffic surface
                    // reaches Sentry with no user or club to pivot on.
                    defaultEmail={authUser?.email}
                    errorContext={{ userId: cardUserId, clubId: club?.id }}
                    formId={CARD_FORM_ID}
                    hideSubmitButton
                    onReadyChange={setCardReady}
                    onSubmittingChange={setCardSubmitting}
                    onMethodChange={setNewMethodKind}
                  />

                  {/* Provider branding sits with the card fields it describes —
                      not under the saved-methods list, where no card is entered. */}
                  {paymentProvider === 'stripe' && (
                    <p className="text-center text-xs text-butter-gray-500" data-testid="checkout-powered-by-stripe">
                      Powered by <span className="font-semibold text-butter-gray-700">Stripe</span>
                    </p>
                  )}

                  {/* Back to saved methods button (if applicable) */}
                  {isAuthenticated &&
                    savedPaymentMethods.length > 0 &&
                    showNewPaymentForm && (
                      <button
                        type="button"
                        onClick={() => setShowNewPaymentForm(false)}
                        disabled={busy}
                        className="mt-4 text-sm text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        ← Back to saved payment methods
                      </button>
                    )}
                </>
              )}
            </CheckoutSection>
          )}

          {/* Waiver — between payment details and the checkout button, so it is
              signed before the one button that saves the card can be pressed.
              Renders nothing when the club has no waiver, or (for a member) when
              nothing is outstanding. */}
          {paymentStep === "payment" && (
            <WaiverSection clubId={club?.id} disabled={busy} divider />
          )}

          {/* The single checkout button, for guest and member alike. `submit-card`
              drives the card form through the native `form=` attribute; its own
              save button is hidden. */}
          {paymentStep === "payment" && (
            <div className="space-y-4 pt-4">
              <button
                type={cta.action === "submit-card" ? "submit" : "button"}
                form={cta.action === "submit-card" ? CARD_FORM_ID : undefined}
                onClick={handleCtaClick}
                // Unsigned waivers (and an unknown price, for a member) disable the
                // button rather than failing on click — the design greys it out.
                disabled={cta.disabled}
                aria-busy={busy}
                className={CTA_CLASSES}
                data-testid={cta.testId}
              >
                {cta.label}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sign-In Modal (shown when existing user detected) */}
      <SignInModal
        isOpen={paymentStep === "sign-in"}
        onClose={() => setPaymentStep("email")}
        onSuccess={handleSignInSuccess}
        prefillEmail={state.email}
      />

      <EarlyBookingConfirmDialog
        open={earlyConfirm.open}
        startTime={selectedStartTime}
        endTime={selectedEndTime}
        onConfirm={earlyConfirm.confirm}
        onCancel={earlyConfirm.cancel}
      />
    </div>
  );
}
